import { useEffect, useMemo, useState } from 'react';
import { DirectionsLink } from '../../components/DirectionsLink';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import { LocateMeControl } from '../../components/maps/LocateMeControl';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { addDays, format, isToday, parseISO, startOfWeek } from 'date-fns';
import {
  ArrowRight,
  ArrowRightLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Map as MapIcon,
  MapPin,
} from 'lucide-react';
import {
  scheduleService,
  type WeeklyPlanSite,
} from '../../services/scheduleService';
import { useToast } from '@/hooks/useToast';
import { DayPicker } from './components/DayPicker';
import { DAY_COLORS, WEEKDAYS, dayColorFromName } from '../../utils/dayColors';

// Match the Leaflet icon fix used by LiteSiteDetailPage. Without this, Vite
// strips the default marker icon URLs and pins render as broken images.
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })
  ._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function getMonday(d: Date): string {
  return format(startOfWeek(d, { weekStartsOn: 1 }), 'yyyy-MM-dd');
}

// Teardrop pin colored by the inspection's scheduled weekday (see dayColors).
// Cached per color so panning/zooming doesn't rebuild icons every render.
const _dayIconCache = new Map<string, L.DivIcon>();
function dayColoredIcon(color: string): L.DivIcon {
  const cached = _dayIconCache.get(color);
  if (cached) return cached;
  const icon = L.divIcon({
    className: 'day-colored-pin',
    html: `<div style="width:18px;height:18px;background:${color};border:2px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 18],
    popupAnchor: [0, -16],
  });
  _dayIconCache.set(color, icon);
  return icon;
}

// Imperatively re-fit the map to the visible markers whenever the set changes.
// Wrapped in a child component so it can call `useMap` from inside the
// MapContainer's React context.
function FitToSites({ sites }: { sites: WeeklyPlanSite[] }) {
  const map = useMap();
  useEffect(() => {
    if (sites.length === 0) return;
    if (sites.length === 1) {
      map.setView([sites[0].latitude, sites[0].longitude], 13);
      return;
    }
    const bounds = L.latLngBounds(
      sites.map((s) => [s.latitude, s.longitude] as [number, number])
    );
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [sites, map]);
  return null;
}

export function LiteMapsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
  const [selectedDate, setSelectedDate] = useState(() =>
    format(new Date(), 'yyyy-MM-dd')
  );

  const currentWeekStart = useMemo(
    () => getMonday(parseISO(selectedDate)),
    [selectedDate]
  );

  const { data: plan, isLoading } = useQuery({
    queryKey: ['weekly-plan', currentWeekStart],
    queryFn: () => scheduleService.getWeeklyPlan(currentWeekStart),
  });

  const weekDays = plan?.days ?? [];

  // Sites visible in the current view. Pins shouldn't render for a site that
  // lacks coordinates — the map can't place them and Leaflet errors on NaN.
  const visibleSites = useMemo<WeeklyPlanSite[]>(() => {
    const raw =
      viewMode === 'day'
        ? (plan?.days.find((d) => d.date === selectedDate)?.sites ?? [])
        : (plan?.days.flatMap((d) => d.sites) ?? []);
    return raw.filter(
      (s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude)
    );
  }, [plan, viewMode, selectedDate]);

  // De-duplicate when in week mode — the same site can show up on multiple
  // days (clones / multi-visit) and stacking 3 pins on identical coords is
  // confusing. Group by site_id and keep the earliest inspection.
  const dedupedSites = useMemo(() => {
    if (viewMode === 'day') return visibleSites;
    const seen = new Map<string, WeeklyPlanSite>();
    for (const s of visibleSites) {
      const existing = seen.get(s.site_id);
      if (!existing || s.scheduled_date < existing.scheduled_date) {
        seen.set(s.site_id, s);
      }
    }
    return Array.from(seen.values());
  }, [visibleSites, viewMode]);

  const rescheduleMutation = useMutation({
    mutationFn: ({
      inspectionId,
      newDate,
    }: {
      inspectionId: string;
      newDate: string;
    }) => scheduleService.rescheduleInspection(inspectionId, newDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-plan'] });
      toast({ title: 'Inspection rescheduled' });
    },
    onError: () =>
      toast({ title: 'Failed to reschedule', variant: 'destructive' }),
  });

  // Center: first site → fallback to Dallas-area so the user sees something
  // useful while the schedule loads or when their plan is empty.
  const mapCenter: [number, number] =
    dedupedSites.length > 0
      ? [dedupedSites[0].latitude, dedupedSites[0].longitude]
      : [32.7767, -96.797];

  const weekLabel = useMemo(() => {
    const start = parseISO(currentWeekStart);
    const end = addDays(start, 4);
    return `Week of ${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
  }, [currentWeekStart]);

  // Mon–Sun pills for the current week (weekends since JIM-1030-08). Computed locally (not from `plan`) so the
  // selector stays put while the weekly plan loads; colors follow the fixed
  // day-of-week scheme (see dayColors), and the date is shown DD/MM per Jim.
  const weekPills = useMemo(() => {
    const monday = parseISO(currentWeekStart);
    return WEEKDAYS.map((dayName, i) => {
      const date = addDays(monday, i);
      return {
        dayName,
        short: dayName.slice(0, 3),
        dateStr: format(date, 'yyyy-MM-dd'),
        label: format(date, 'dd/MM'),
        color: DAY_COLORS[dayName],
        isToday: isToday(date),
      };
    });
  }, [currentWeekStart]);

  const viewingThisWeek = currentWeekStart === getMonday(new Date());

  const shiftWeek = (deltaWeeks: number) =>
    setSelectedDate(
      format(addDays(parseISO(selectedDate), deltaWeeks * 7), 'yyyy-MM-dd')
    );

  const goToToday = () => {
    setViewMode('day');
    setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
  };

  return (
    <div className="space-y-3">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-soft border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
              <MapIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-slate-900 dark:text-white truncate">
                Maps
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {weekLabel}
              </p>
            </div>
          </div>
          {/* Jump back to the current week — only shown when viewing another week */}
          {!viewingThisWeek && (
            <button
              onClick={goToToday}
              className="px-2.5 py-1 text-xs font-medium rounded-md text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors flex-shrink-0"
            >
              Today
            </button>
          )}
        </div>

        {/* Week navigator: prev/next-week arrows + Mon–Sun day pills + Full Week.
            Each pill carries its day-of-week color, so this row also serves as
            the legend (the old bottom color key is gone). Tap a day to filter
            the map to that day; tap Full Week to show every site at once. */}
        <div className="px-2 py-2 bg-slate-50 dark:bg-slate-700/30 border-b border-slate-100 dark:border-slate-700 flex items-center gap-1">
          <button
            onClick={() => shiftWeek(-1)}
            className="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex-shrink-0"
            aria-label="Previous week"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-0.5 flex-1 justify-center overflow-x-auto">
            {weekPills.map((p) => {
              const active = viewMode === 'day' && selectedDate === p.dateStr;
              return (
                <button
                  key={p.dateStr}
                  onClick={() => {
                    setViewMode('day');
                    setSelectedDate(p.dateStr);
                  }}
                  aria-pressed={active}
                  title={`${p.dayName} ${p.label}`}
                  className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg border transition-colors flex-shrink-0 ${
                    active
                      ? 'border-primary-500 bg-white dark:bg-slate-800 shadow-sm'
                      : p.isToday
                        ? 'border-primary-200 dark:border-primary-800 hover:bg-slate-200/60 dark:hover:bg-slate-600/40'
                        : 'border-transparent hover:bg-slate-200/60 dark:hover:bg-slate-600/40'
                  }`}
                >
                  <span className="flex items-center gap-1 text-xs font-medium text-slate-700 dark:text-slate-200">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: p.color }}
                    />
                    {p.short}
                  </span>
                  <span
                    className={`text-[10px] tabular-nums ${
                      active
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-slate-400 dark:text-slate-500'
                    }`}
                  >
                    {p.label}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setViewMode('week')}
            aria-pressed={viewMode === 'week'}
            className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors whitespace-nowrap flex-shrink-0 ${
              viewMode === 'week'
                ? 'border-primary-500 bg-white dark:bg-slate-800 text-primary-700 dark:text-primary-300 shadow-sm'
                : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-600/40'
            }`}
          >
            Full Week
          </button>

          <button
            onClick={() => shiftWeek(1)}
            className="p-1.5 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors flex-shrink-0"
            aria-label="Next week"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="h-[480px] flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
          </div>
        ) : dedupedSites.length === 0 ? (
          <div className="h-[480px] flex flex-col items-center justify-center px-6 text-center">
            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-3">
              <MapPin className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              No sites scheduled
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xs">
              {viewMode === 'day'
                ? 'Nothing on the map for this day. Pick another day above, or tap Full Week.'
                : 'Your weekly plan is empty. Add sites from the Schedule tab.'}
            </p>
            <Link
              to="/lite/schedule"
              className="mt-4 px-3 py-1.5 text-xs font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              Open Schedule
            </Link>
          </div>
        ) : (
          <div className="h-[480px] relative z-0">
            <MapContainer
              center={mapCenter}
              zoom={11}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitToSites sites={dedupedSites} />
              {dedupedSites.map((site) => {
                // Day-of-week drives the pin color. Prefer the plan's labeled
                // day for this date; fall back to deriving it from the date.
                const dayName =
                  weekDays.find((d) => d.date === site.scheduled_date)
                    ?.day_of_week ??
                  (site.scheduled_date
                    ? format(parseISO(site.scheduled_date), 'EEEE')
                    : null);
                return (
                  <SitePin
                    key={site.inspection_id}
                    site={site}
                    dayName={dayName}
                    weekDays={weekDays}
                    onReschedule={(newDate) =>
                      rescheduleMutation.mutate({
                        inspectionId: site.inspection_id,
                        newDate,
                      })
                    }
                  />
                );
              })}
              <LocateMeControl />
            </MapContainer>
          </div>
        )}
      </div>

      {/* Site count — the colored day pills above now serve as the legend. */}
      {!isLoading && dedupedSites.length > 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400 px-1">
          {dedupedSites.length} site{dedupedSites.length === 1 ? '' : 's'} on
          the map
          {viewMode === 'week' ? ' · full week' : ''}
        </p>
      )}
    </div>
  );
}

// Pin + popover. Route-planning focused: a "View site details" link (added at
// Jim's request, 2026-06) plus reschedule. Still intentionally NO "Start
// Inspection" shortcut here — starting a visit stays on the Sites/Schedule tabs
// so route-planning isn't confused with conducting an inspection.
function SitePin({
  site,
  dayName,
  weekDays,
  onReschedule,
}: {
  site: WeeklyPlanSite;
  dayName: string | null;
  weekDays: import('../../services/scheduleService').WeeklyPlanDay[];
  onReschedule: (newDate: string) => void;
}) {
  const [showPicker, setShowPicker] = useState(false);
  return (
    <Marker
      position={[site.latitude, site.longitude]}
      icon={dayColoredIcon(dayColorFromName(dayName))}
    >
      {/* closeOnClick={false} keeps the popover open when the inspector taps
          "Schedule for another day". Without this, Leaflet treats the button
          click as a map click and dismisses the popover before the DayPicker
          can swap in. */}
      <Popup closeOnClick={false} autoClose={false}>
        <div className="space-y-2 min-w-[200px]">
          <div>
            <Link
              to={`/lite/sites/${site.site_id}`}
              className="text-sm font-semibold text-slate-900 hover:text-primary-600"
            >
              {site.site_name}
            </Link>
            <p className="text-xs text-slate-500 mt-0.5">
              {site.client_name && `${site.client_name} · `}
              {site.city}, {site.state}
              {site.scheduled_date && (
                <>
                  {' · '}
                  {format(parseISO(site.scheduled_date), 'EEE MMM d')}
                </>
              )}
            </p>
          </div>
          {/* Actions: site detail link sits ABOVE reschedule (Jim, 2026-06) */}
          <div className="flex flex-col gap-1.5">
            <DirectionsLink
              latitude={site.latitude}
              longitude={site.longitude}
              className="justify-center"
            />
            <Link
              to={`/lite/sites/${site.site_id}`}
              className="flex items-center justify-between gap-1 px-2 py-1 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md"
            >
              <span>View site details</span>
              <ArrowRight className="w-3 h-3" />
            </Link>
            {showPicker ? (
              <DayPicker
                weekDays={weekDays}
                currentDate={site.scheduled_date}
                onSelect={(newDate) => {
                  setShowPicker(false);
                  onReschedule(newDate);
                }}
                onClose={() => setShowPicker(false)}
              />
            ) : (
              <button
                onClick={() => setShowPicker(true)}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md"
              >
                <ArrowRightLeft className="w-3 h-3" />
                Schedule for another day
              </button>
            )}
          </div>
        </div>
      </Popup>
    </Marker>
  );
}
